<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.tax_rules.form.country')"
            :is-required="true"
            :hint="__('settings.tax_rules.form.country_hint')"
        >
            <DSelect
                id="country_code"
                v-model="form.country_code"
                :disabled="isDefaultTaxRule"
                :options="countries"
                add-input-classes="bg-light"
                :placeholder="'Select country'"
                :is-valid="isFormFieldValid('country_code')"
                :invalid-feedback="errors.country_code"
            />
        </DFormRow>

        <DynamicTable
            v-model="form.tax_rule_rates"
            :fields="getFields"
            :transformers="transformers"
            :new-row-data="getNewRowData()"
            :modal-title-add="__('settings.tax_rules.modal.title_add')"
            :modal-title-edit="__('settings.tax_rules.modal.title_edit')"
            :errors="errorsData"
            error-prefix="tax_rule_rates"
            @update:modelValue="setTaxRates"
        >
            <template #modalForm="{ item }">
                <DFormRow
                    :label="__('settings.tax_rules.form.valid_from')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.valid_from`]"
                    :hint="__('settings.tax_rules.form.valid_from_hint')"
                >
                    <DDateFormatInput
                        id="valid_from"
                        v-model="item.valid_from"
                        format="YYYY-MM-DD"
                        type="date"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax_rules.form.tax_rate')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.tax_rate`]"
                    :hint="__('settings.tax_rules.form.tax_rate_hint')"
                >
                    <DInput
                        id="tax_rate"
                        v-model="item.tax_rate"
                        add-input-classes="bg-light"
                        maxlength="5"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax_rules.form.tax_key')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.tax_key`]"
                    :hint="__('settings.tax_rules.form.tax_key_hint')"
                >
                    <DInput
                        id="tax_key"
                        v-model="item.tax_key"
                        add-input-classes="bg-light"
                        type="string"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isFinanceSystemNetsuite === false"
                    :label="__('settings.tax_rules.form.revenue_account')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.revenue_account`]"
                    :hint="__('settings.tax_rules.form.revenue_account_hint')"
                >
                    <DInput
                        id="revenue_account"
                        v-model="item.revenue_account"
                        add-input-classes="bg-light"
                        type="number"
                        min="0"
                        max="2147483647"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax_rules.form.account_debt_loss')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.account_debt_loss`]"
                    :hint="__('settings.tax_rules.form.account_debt_loss_hint')"
                >
                    <DInput
                        id="account_debt_loss"
                        v-model="item.account_debt_loss"
                        add-input-classes="bg-light"
                        type="number"
                        min="1"
                        max="10000000"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax_rules.form.tax_rate_with_vat')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.tax_rate_with_vat`]"
                    :hint="__('settings.tax_rules.form.tax_rate_with_vat_hint')"
                >
                    <DInput
                        id="tax_rate_with_vat"
                        v-model="item.tax_rate_with_vat"
                        add-input-classes="bg-light"
                        maxlength="5"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax_rules.form.tax_key_with_vat')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.tax_key_with_vat`]"
                    :hint="__('settings.tax_rules.form.tax_key_with_vat_hint')"
                >
                    <DInput
                        id="tax_key_with_vat"
                        v-model="item.tax_key_with_vat"
                        add-input-classes="bg-light"
                        type="string"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isFinanceSystemNetsuite === false"
                    :label="__('settings.tax_rules.form.revenue_account_with_vat')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.revenue_account_with_vat`]"
                    :hint="__('settings.tax_rules.form.revenue_account_with_vat_hint')"
                >
                    <DInput
                        id="revenue_account_with_vat"
                        v-model="item.revenue_account_with_vat"
                        add-input-classes="bg-light"
                        type="number"
                        min="0"
                        max="2147483647"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tax_rules.form.account_debt_loss_with_vat')"
                    :is-required="true"
                    :error="errorsData[`tax_rule_rates.${item.index}.account_debt_loss_with_vat`]"
                    :hint="__('settings.tax_rules.form.account_debt_loss_with_vat_hint')"
                >
                    <DInput
                        id="account_debt_loss_with_vat"
                        v-model="item.account_debt_loss_with_vat"
                        add-input-classes="bg-light"
                        type="number"
                        min="1"
                        max="10000000"
                    />
                </DFormRow>
            </template>
        </DynamicTable>

        <DFormRow
            :label="__('settings.tax_rules.form.effectivity')"
            :is-required="true"
            :hint="__('settings.tax_rules.form.effectivity_hint')"
        >
            <DSelect
                id="effectivity"
                v-model="form.effectivity"
                :disabled="isDefaultTaxRule"
                :options="effectiveness"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('effectivity')"
                :invalid-feedback="errors.effectivity"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.tax_rules.form.invoice_text')"
            :hint="__('settings.tax_rules.form.invoice_text_hint')"
        >
            <DFormTextarea
                id="invoice_text"
                v-model="form.invoice_text"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('invoice_text')"
                :invalid-feedback="errors.invoice_text"
                maxlength="255"
            />
            <TranslationLink
                :tenant-languages="tenantLanguages"
                :route="$route('settings.tax-rules.translations', {taxRule: taxRule?.id ?? 0})"
                :default-tenant-language="defaultTenantLanguage"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.tax_rules.form.invoice_text_with_vat')"
            :hint="__('settings.tax_rules.form.invoice_text_with_vat_hint')"
        >
            <DFormTextarea
                id="invoice_text_with_vat"
                v-model="form.invoice_text_with_vat"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('invoice_text_with_vat')"
                :invalid-feedback="errors.invoice_text_with_vat"
                maxlength="255"
            />
            <TranslationLink
                :tenant-languages="tenantLanguages"
                :route="$route('settings.tax-rules.translations', {taxRule: taxRule?.id ?? 0})"
                :default-tenant-language="defaultTenantLanguage"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.tax_rules.form.second_country_code')"
            :is-required="false"
            :hint="__('settings.tax_rules.form.second_country_code_hint')"
        >
            <DInput
                id="second_country_code"
                v-model="form.second_country_code"
                add-input-classes="bg-light"
                :is-valid="isFormFieldValid('second_country_code')"
                :invalid-feedback="errors.second_country_code"
                :placeholder="__('settings.tax_rules.form.second_country_code_placeholder')"
                maxlength="2"
            />
        </DFormRow>
        <DFormControls
            :cancel-route="$route('settings.tax-rules.index')"
            :dirty="form.isDirty"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>

import TranslationLink from '../TranslationLink.vue'
import DynamicTable from '~@/Components/Common/DynamicComponents/DynamicTable.vue'
export default {
    name: 'TaxRuleForm',
    components: { DynamicTable, TranslationLink },
    props: {
        countries: {
            type: Array,
            default: () => { return [] }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        taxRule: {
            type: Object,
            default: () => { return {} }
        },
        defaultTenantLanguage: {
            type: String,
            default: ''
        },
        tenantLanguages: {
            type: Array,
            default: () => { return [] }
        },
        effectiveness: {
            type: Array,
            default: () => { return [] }
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: () => { return false }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                country_code: this.taxRule?.country_code ?? this.$page.props.defaultCountry,
                invoice_text: this.taxRule?.invoice_text ?? null,
                invoice_text_with_vat: this.taxRule?.invoice_text_with_vat ?? null,
                second_country_code: this.taxRule?.second_country_code ?? null,
                effectivity: this.taxRule?.effectivity ?? null,
                tax_rule_rates: []
            }),
            transformers: {
                valid_from: (item) => this.setDateFormat(item, 'DD.MM.YYYY'),
                valid_until: (item) => this.setDateFormat(item, 'DD.MM.YYYY'),
                tax_rate: (item) => item ? this.taxRateFormat(item) : '',
                tax_rate_with_vat: (item) => item ? this.taxRateFormat(item) : ''
            },
            errorsData: this.errors
        }
    },
    computed: {
        isDefaultTaxRule () {
            return this.taxRule && this.taxRule.id && !this.taxRule.country_code
        },
        getFields () {
            if (this.isFinanceSystemNetsuite === true) {
                return [
                    { key: 'valid_from', label: this.__('settings.tax_rules.form.valid_from') },
                    { key: 'valid_until', label: this.__('settings.tax_rules.form.valid_until') },
                    { key: 'tax_rate', label: this.__('settings.tax_rules.form.tax_rate') },
                    { key: 'tax_key', label: this.__('settings.tax_rules.form.tax_key') },
                    { key: 'account_debt_loss', label: this.__('settings.tax_rules.form.account_debt_loss') },
                    { key: 'tax_rate_with_vat', label: this.__('settings.tax_rules.form.tax_rate_with_vat') },
                    { key: 'tax_key_with_vat', label: this.__('settings.tax_rules.form.tax_key_with_vat') },
                    { key: 'account_debt_loss_with_vat', label: this.__('settings.tax_rules.form.account_debt_loss_with_vat') },
                    { key: 'actions', label: '' }
                ]
            }

            return [
                { key: 'valid_from', label: this.__('settings.tax_rules.form.valid_from') },
                { key: 'valid_until', label: this.__('settings.tax_rules.form.valid_until') },
                { key: 'tax_rate', label: this.__('settings.tax_rules.form.tax_rate') },
                { key: 'tax_key', label: this.__('settings.tax_rules.form.tax_key') },
                { key: 'revenue_account', label: this.__('settings.tax_rules.form.revenue_account') },
                { key: 'account_debt_loss', label: this.__('settings.tax_rules.form.account_debt_loss') },
                { key: 'tax_rate_with_vat', label: this.__('settings.tax_rules.form.tax_rate_with_vat') },
                { key: 'tax_key_with_vat', label: this.__('settings.tax_rules.form.tax_key_with_vat') },
                { key: 'revenue_account_with_vat', label: this.__('settings.tax_rules.form.revenue_account_with_vat') },
                { key: 'account_debt_loss_with_vat', label: this.__('settings.tax_rules.form.account_debt_loss_with_vat') },
                { key: 'actions', label: '' }
            ]
        }
    },
    watch: {
        errors (newErrors) {
            this.errorsData = newErrors
        }
    },
    created () {
        this.setTaxRates(this.taxRule?.tax_rule_rates ?? [])
    },
    methods: {
        submitForm () {
            this.$emit('form-submitted', this.form)
        },
        setTaxRates (data) {
            this.errorsData = {}
            // Sort by start date
            data = data.sort(function (a, b) {
                // Turn your strings into dates, and then subtract them
                // to get a value that is either negative, positive, or zero.
                return new Date(b.valid_from) - new Date(a.valid_from)
            })

            const rates = []

            let indexTrack = -1

            data.forEach((rate, index) => {
                const startDateOfNextRecord = data[index - 1]?.valid_from ?? null
                const date = startDateOfNextRecord ? new Date(startDateOfNextRecord) : null

                rates.push({
                    index: ++indexTrack,
                    id: rate.id,
                    valid_from: rate.valid_from,
                    valid_until: date !== null ? new Date(date.setDate(date.getDate() - 1)).toISOString() : null,
                    tax_rate: rate.tax_rate,
                    tax_key: rate.tax_key,
                    revenue_account: rate.revenue_account,
                    account_debt_loss: rate.account_debt_loss,
                    tax_rate_with_vat: rate.tax_rate_with_vat,
                    tax_key_with_vat: rate.tax_key_with_vat,
                    revenue_account_with_vat: rate.revenue_account_with_vat,
                    account_debt_loss_with_vat: rate.account_debt_loss_with_vat
                })
            })

            this.form.tax_rule_rates = rates
        },
        getNewRowData () {
            return {
                index: 0,
                id: null,
                valid_from: null,
                valid_until: null,
                tax_rate: null,
                tax_key: null,
                revenue_account: this.isFinanceSystemNetsuite === false ? null : 0,
                account_debt_loss: null,
                tax_rate_with_vat: null,
                tax_key_with_vat: null,
                revenue_account_with_vat: this.isFinanceSystemNetsuite === false ? null : 0,
                account_debt_loss_with_vat: null
            }
        },
        taxRateFormat (value) {
            return parseFloat(value).toFixed(2)
        }
    }
}
</script>

<style lang="scss" scoped>

</style>
